/**
 * @fileoverview UniversalSearchBar — typeahead + Ask AI
 *
 * Renders a single search input that:
 * - Debounces user input and calls the scoped universal-search query
 *   (workspace or single-job).
 * - Surfaces typeahead results grouped by `kind` with clickable rows
 *   that deep-link to the entity's home (asset list, contact panel,
 *   document detail, etc.).
 * - Exposes an "Ask AI" affordance that promotes the current query to
 *   a full chat message via the parent's `onAskAi` callback.
 *
 * Visually modelled on the Convex docs search example the user
 * referenced: a single bar that mixes navigation hits with an AI
 * call-to-action above the result list.
 *
 * Used by:
 * - `JobAgentView` (job scope)
 * - `WorkspaceAgentView` (workspace scope)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { useAction } from "convex/react";
import { Link } from "@tanstack/react-router";
import {
  Search,
  Sparkles,
  ArrowRight,
  Loader2,
  FileText,
  CalendarDays,
} from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives/badge";
import { getKindMeta, resolveResultBadge } from "./searchResultKinds";
import type { SemanticSearchResult } from "@cvx/agents/universal/searchSemantic.mapping";
import type { UniversalSearchResult } from "@cvx/agents/universal/search";
import { CATEGORY_META } from "@/calendar/categoryMeta";
import { useAllOrgKeyDates } from "@/calendar/useAllOrgKeyDates";
import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";

/** Minimal calendar-event shape consumed by the search bar's "Key dates"
 *  section. The `listForJob` / `listForOrg` queries return more fields; we
 *  only read these. */
interface CalendarHit {
  id: string;
  jobId: string;
  jobName: string;
  title: string;
  description: string | null;
  date: string;
  category: KeyDateCategory;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SearchResult = UniversalSearchResult;

export interface UniversalSearchBarProps {
  /** Job-scoped search; mutually exclusive with `orgId`. */
  jobId?: Id<"jobs">;
  /** Workspace-scoped search; mutually exclusive with `jobId`. */
  orgId?: Id<"organizations">;
  /**
   * Called when the user presses Enter or clicks "Ask AI". Receives the
   * current query string. The parent wires this to the chat composer.
   */
  onAskAi?: (query: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Dropdown opening direction. "down" (default) is for top-of-page
   * placements; "up" is for chat-composer placements where the input
   * is anchored to the bottom and the dropdown should grow above it.
   */
  direction?: "down" | "up";
  /** Send button label. Defaults to "Ask Morgan". */
  askButtonLabel?: string;
  /**
   * When true, the bar is used as a chat composer — the input becomes
   * multi-line capable (Shift+Enter for newlines) and the "Ask" button
   * is always visible (not gated on `value.trim()`).
   */
  composer?: boolean;
  /**
   * When true, the results panel is rendered inline (in the natural
   * document flow) instead of as an `absolute` floating dropdown. Used
   * by the Cmd+K command palette so the modal grows to fit the
   * results instead of letting them spill off the viewport.
   */
  embedded?: boolean;
  /**
   * Fires after a result row is clicked (after the deep-link navigation
   * is triggered). The parent uses this to close any wrapping modal —
   * the bar can't close a modal it doesn't own.
   */
  onResultClick?: () => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UniversalSearchBar({
  jobId,
  orgId,
  onAskAi,
  placeholder,
  autoFocus,
  direction = "down",
  askButtonLabel = "Ask Morgan",
  composer = false,
  embedded = false,
  onResultClick,
  className,
}: UniversalSearchBarProps) {
  const [value, setValue] = useState("");
  const debounced = useDebouncedValue(value.trim(), 220);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  // We must construct ONLY ONE of the two scoped queries with real
  // Convex args — `convexQueryOrIdle` keeps the inactive scope from
  // hitting the query's arg validator.
  const isJobScope = !!jobId;
  const isWorkspaceScope = !isJobScope && !!orgId;

  const jobQuery = useQuery({
    ...convexQueryOrIdle(
      api.agents.universal.search.searchJob,
      isJobScope ? { jobId, query: debounced } : undefined,
    ),
    enabled: isJobScope && debounced.length >= 2,
  });

  const workspaceQuery = useQuery({
    ...convexQueryOrIdle(
      api.agents.universal.search.searchWorkspace,
      isWorkspaceScope ? { orgId, query: debounced } : undefined,
    ),
    enabled: isWorkspaceScope && debounced.length >= 2,
  });

  const activeQuery = isJobScope ? jobQuery : workspaceQuery;
  const results: SearchResult[] = useMemo(
    () => activeQuery.data ?? [],
    [activeQuery.data],
  );
  const isLoading = activeQuery.isFetching && debounced.length >= 2;

  // Semantic (meaning-based) document-content search runs in parallel to the
  // keyword navigation hits above. It needs an action (query embedding +
  // vector search aren't available in reactive queries), so it's a debounced
  // TanStack query over the Convex action. Gated at length >= 3 to keep the
  // embedding cost down.
  const semanticJobAction = useAction(
    api.agents.universal.searchSemantic.semanticSearchJob,
  );
  const semanticWorkspaceAction = useAction(
    api.agents.universal.searchSemantic.semanticSearchWorkspace,
  );
  const semanticQuery = useQuery({
    queryKey: [
      "semanticSearch",
      isJobScope ? "job" : "workspace",
      String(jobId ?? orgId ?? ""),
      debounced,
    ],
    queryFn: (): Promise<SemanticSearchResult[]> => {
      if (isJobScope) {
        return semanticJobAction({ jobId, query: debounced, limit: 6 });
      }
      if (isWorkspaceScope) {
        return semanticWorkspaceAction({ orgId, query: debounced, limit: 6 });
      }
      // Unreachable — the `enabled` gate requires one active scope.
      return Promise.resolve([]);
    },
    enabled: (isJobScope || isWorkspaceScope) && debounced.length >= 3,
    staleTime: 30_000,
  });
  const semanticResults: SemanticSearchResult[] = semanticQuery.data ?? [];
  const semanticLoading = semanticQuery.isFetching && debounced.length >= 3;

  // Calendar / key-date matches. Reuses the existing reactive calendar feeds
  // (one cached subscription, args don't include the query) and filters
  // client-side by the search term.
  const jobCalendarQuery = useQuery({
    ...convexQueryOrIdle(
      api.keyDates.queries.listForJob,
      isJobScope ? { jobId } : undefined,
    ),
    enabled: isJobScope && debounced.length >= 2,
  });
  // Workspace scope streams the org feed in pages (MOR-2473) — the single
  // array query breaks past 8192 events — and stays idle until there is a
  // term worth matching.
  const { events: workspaceCalendarEvents } = useAllOrgKeyDates({
    orgId: isWorkspaceScope && debounced.length >= 2 ? orgId : undefined,
  });
  const calendarEvents: CalendarHit[] = useMemo(
    () =>
      isJobScope ? (jobCalendarQuery.data ?? []) : workspaceCalendarEvents,
    [isJobScope, jobCalendarQuery.data, workspaceCalendarEvents],
  );
  const calendarHits = useMemo(() => {
    if (debounced.length < 2) return [];
    const needle = debounced.toLowerCase();
    return calendarEvents
      .filter((e) => {
        const catLabel = CATEGORY_META[e.category]?.label ?? e.category;
        return (
          e.title.toLowerCase().includes(needle) ||
          (e.description ?? "").toLowerCase().includes(needle) ||
          e.jobName.toLowerCase().includes(needle) ||
          catLabel.toLowerCase().includes(needle)
        );
      })
      .slice(0, 6);
  }, [calendarEvents, debounced]);

  // Group results by kind for display sections.
  const grouped = useMemo(() => {
    const map = new Map<string, SearchResult[]>();
    for (const r of results) {
      const list = map.get(r.kind) ?? [];
      list.push(r);
      map.set(r.kind, list);
    }
    return Array.from(map.entries());
  }, [results]);

  // Close dropdown when clicking outside.
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const handleAskAi = useCallback(() => {
    if (!value.trim() || !onAskAi) return;
    onAskAi(value.trim());
    setValue("");
    setOpen(false);
  }, [value, onAskAi]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (value.trim()) handleAskAi();
      } else if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    },
    [value, handleAskAi],
  );

  const showDropdown = open && debounced.length >= 2;
  const canAsk = value.trim().length > 0;

  // Embedded mode: render results in the natural document flow so the
  // wrapping container (e.g. a modal) bounds them and can scroll. The
  // wrapper must be `flex flex-col` and the dropdown takes the
  // remaining vertical space with its own scroll. Floating-dropdown
  // mode is reserved for in-page placements.
  const dropdownPositionClass = embedded
    ? "relative w-full mt-2 flex-1 min-h-0"
    : direction === "up"
      ? "absolute left-0 right-0 bottom-full mb-2"
      : "absolute left-0 right-0 top-full mt-2";

  // In composer mode the input is a textarea that grows up to a few
  // lines. In search mode it's a single-line input.
  const inputElement = composer ? (
    <textarea
      ref={(el) => {
        inputRef.current = el;
      }}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        setOpen(true);
        // Auto-grow up to a sane cap.
        const el = e.target;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
      }}
      onFocus={() => setOpen(true)}
      onKeyDown={handleKeyDown}
      autoFocus={autoFocus}
      rows={1}
      placeholder={
        placeholder ??
        (jobId
          ? "Search this job, or ask Morgan…"
          : "Search across your jobs, or ask Morgan…")
      }
      className="max-h-[140px] min-w-0 flex-1 resize-none bg-transparent text-sm leading-relaxed outline-none placeholder:text-muted-foreground/60"
    />
  ) : (
    <input
      ref={(el) => {
        inputRef.current = el;
      }}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        setOpen(true);
      }}
      onFocus={() => setOpen(true)}
      onKeyDown={handleKeyDown}
      autoFocus={autoFocus}
      placeholder={
        placeholder ??
        (jobId
          ? "Search this job, or ask Morgan a question..."
          : "Search across your jobs, or ask Morgan a question...")
      }
      className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
    />
  );

  return (
    <div ref={wrapperRef} className={cn("relative w-full", className)}>
      <div
        className={cn(
          "flex shrink-0 gap-2 rounded-xl border border-border bg-background shadow-sm transition-shadow",
          composer ? "items-end px-3.5 py-2.5" : "items-center px-3.5 py-2.5",
          open && "border-primary/40 shadow-md",
        )}
      >
        <Search
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground",
            composer && "mb-1.5",
          )}
        />
        {inputElement}
        {(canAsk || composer) && (
          <button
            type="button"
            onClick={handleAskAi}
            disabled={!canAsk}
            className={cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium shadow-sm transition-colors",
              canAsk
                ? "bg-primary text-primary-foreground hover:bg-primary/85"
                : "bg-muted text-muted-foreground/50",
            )}
          >
            <Sparkles className="h-3 w-3" />
            {askButtonLabel}
            <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className={cn(
            dropdownPositionClass,
            "z-50 overflow-y-auto rounded-xl border border-border bg-popover shadow-lg",
            // Embedded mode lives inside a sized parent (e.g. a modal's
            // flex column) so let the parent control height. Floating
            // dropdowns are clamped to the viewport.
            embedded ? "max-h-full" : "max-h-[60vh]",
          )}
        >
          {/* Loading state */}
          {isLoading && results.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Searching...
            </div>
          )}
          {/* Empty state — only when no navigation matches AND not in
              composer mode (composer mode already has the Ask button
              right next to the input). */}
          {!isLoading &&
            results.length === 0 &&
            semanticResults.length === 0 &&
            calendarHits.length === 0 &&
            !semanticLoading &&
            debounced.length >= 2 &&
            !composer && (
              <div className="px-4 py-3 text-xs text-muted-foreground">
                No direct matches. Press{" "}
                <kbd className="rounded border border-border bg-card px-1 py-0.5 text-[10px] font-medium">
                  ⏎
                </kbd>{" "}
                to ask Morgan.
              </div>
            )}

          {/* Result groups */}
          {grouped.length > 0 && (
            <ul className="py-1.5">
              {grouped.map(([kind, rows]) => {
                const meta = getKindMeta(kind);
                const Icon = meta.icon;
                return (
                  <li key={kind} className="px-1.5 py-0.5">
                    <p className="px-2 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                      {meta.label}
                    </p>
                    {rows.map((r) => {
                      const target = meta.buildTo({
                        id: r.id,
                        jobId: r.jobId ? String(r.jobId) : undefined,
                        orgId: r.orgId ? String(r.orgId) : undefined,
                      });
                      const badge = resolveResultBadge({
                        kind: r.kind,
                        subtitle: r.subtitle,
                        categoryKey: r.categoryKey,
                        statusKey: r.statusKey,
                      });
                      const inner = (
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-card">
                            <Icon className="h-3 w-3 text-muted-foreground" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-foreground">
                              {r.title}
                            </p>
                            {/* Owning-job context for workspace-scope
                                hits ("in Bunnings Group Limited") —
                                critical when multiple matters share a
                                filename like ROCAP.pdf. Omitted for
                                kind=job (the title IS the job) and
                                when the result is already job-scoped. */}
                            {r.jobName && r.kind !== "job" && (
                              <p className="truncate text-[11px] text-muted-foreground/70">
                                in {r.jobName}
                              </p>
                            )}
                          </div>
                          {badge && (
                            <Badge
                              size="sm"
                              variant={badge.variant}
                              color={badge.color}
                              className="shrink-0"
                            >
                              {badge.label}
                            </Badge>
                          )}
                        </div>
                      );
                      if (!target) {
                        return (
                          <div
                            key={r.id}
                            className="rounded-md px-2 py-1.5 opacity-75"
                          >
                            {inner}
                          </div>
                        );
                      }
                      return (
                        <Link
                          key={r.id}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          to={target.to as any}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          params={target.params as any}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          search={target.search as any}
                          onClick={() => {
                            setOpen(false);
                            setValue("");
                            // Bubble up so wrapping modals can close.
                            onResultClick?.();
                          }}
                          className="block rounded-md px-2 py-1.5 transition-colors hover:bg-accent/30"
                        >
                          {inner}
                        </Link>
                      );
                    })}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Calendar / key-date matches. */}
          {calendarHits.length > 0 && (
            <div className="border-t border-border/60 px-1.5 py-1.5">
              <p className="px-2 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                Key dates
              </p>
              {calendarHits.map((e) => {
                const catLabel = CATEGORY_META[e.category]?.label ?? e.category;
                const dateLabel = new Date(
                  `${e.date}T00:00:00`,
                ).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                });
                return (
                  <Link
                    key={e.id}
                    to={
                      isJobScope
                        ? "/dashboard/jobs/$jobId/calendar"
                        : "/dashboard/calendar"
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    params={(isJobScope ? { jobId: e.jobId } : {}) as any}
                    onClick={() => {
                      setOpen(false);
                      setValue("");
                      onResultClick?.();
                    }}
                    className="block rounded-md px-2 py-1.5 transition-colors hover:bg-accent/30"
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-card">
                        <CalendarDays className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">
                          {e.title}
                        </p>
                        <p className="truncate text-[11px] text-muted-foreground/70">
                          {dateLabel} · {catLabel}
                          {isWorkspaceScope && e.jobName
                            ? ` · ${e.jobName}`
                            : ""}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Semantic document-content matches ("In your documents") —
              meaning-based passages from the underlying documents, distinct
              from the keyword navigation hits above. */}
          {(semanticLoading || semanticResults.length > 0) && (
            <div className="border-t border-border/60 px-1.5 py-1.5">
              <p className="flex items-center gap-1.5 px-2 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                In your documents
                {semanticLoading && (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
              </p>
              {semanticResults.map((r, i) => (
                <Link
                  key={`${r.documentId}-${i}`}
                  to="/dashboard/jobs/$jobId/gather"
                  params={{ jobId: r.jobId }}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  search={{ documentId: r.documentId } as any}
                  onClick={() => {
                    setOpen(false);
                    setValue("");
                    onResultClick?.();
                  }}
                  className="block rounded-md px-2 py-1.5 transition-colors hover:bg-accent/30"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border bg-card">
                      <FileText className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
                        <span className="truncate">{r.fileName}</span>
                        {typeof r.pageNumber === "number" && (
                          <span className="shrink-0 text-[10px] font-normal text-muted-foreground/70">
                            p.{r.pageNumber}
                          </span>
                        )}
                      </p>
                      {isWorkspaceScope && r.jobName && (
                        <p className="truncate text-[11px] text-muted-foreground/70">
                          in {r.jobName}
                        </p>
                      )}
                      <p className="line-clamp-2 text-[11px] text-muted-foreground/80">
                        {r.snippet}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
